import type { ReactiveController, ReactiveControllerHost } from "lit";
import { fetch, ResponseError, NetworkError } from "@/utilities";
import { I18n } from "@/i18n/i18n";
import { isPending, type SubmissionStatus } from "@/submission_status";
import { type HandinProgress, IDLE, inProgress } from "@/handin_progress";
import * as Sentry from "@sentry/browser";

const MAX_CODE_BYTES = 256 * 1024;
const SLOW_THRESHOLD_MS = 17_500;
const MAX_BACKOFF_MS = 10_000;
const GIVE_UP_AFTER_MS = 60_000;

function pollDelay(waited: number): number {
    if (waited < 10_000) return 500;
    if (waited < 30_000) return 1000;
    return 5000;
}

async function getErrorMessage(request: Response): Promise<string> {
    let message;
    if (request.status === 422) {
        try {
            const response = await request.json();
            const errors = response.errors;
            if (errors.code && errors.code[0] === "emoji found") {
                message = I18n.t("js.submission-emoji");
            } else if (errors.submission && errors.submission[0] === "rate limited") {
                message = I18n.t("js.submission-rate-limit");
            } else if (errors.code && errors.code[0] === "too long") {
                message = I18n.t("js.submission-too-long");
            } else if (errors.series && errors.series[0] === "not found") {
                message = I18n.t("js.submission-series-removed");
            }
        } catch {
            message = I18n.t("js.submission-failed");
        }
    } else if (request.status === 403) {
        message = I18n.t("js.submission-not-allowed");
    } else if (request.status === 401) {
        message = I18n.t("js.submission-not-logged-in_html");
    }
    return message ?? I18n.t("js.submission-failed");
}

/** What a hand-in posts and reports, read from the host each time it does. */
export type HandinOptions = {
    exerciseId: number;
    courseId: number;
    seriesId: number;
    naturalLanguage: string;
    programmingLanguage: string;
};

type Failed = { ok: false; message: string };

/**
 * The outcome of `handIn`: the queued submission and the series the server saved it in (null when it counts for no
 * series), or why the code was not handed in. `message` can contain markup.
 */
export type HandedIn = { ok: true; submission: SubmissionStatus; seriesId: number | null } | Failed;

/** The outcome of `judge`: the judged submission, or why its result cannot be fetched. */
export type Judged = { ok: true; submission: SubmissionStatus } | Failed;

/**
 * Hands in code and polls its status until it is judged, in two steps its host awaits: `handIn`, then `judge`.
 * It runs one hand-in at a time and asks its host to update whenever `progress` or `submission` changes.
 */
export class HandinJudging implements ReactiveController {
    private _progress: HandinProgress = IDLE;
    private _submission: SubmissionStatus | null = null;
    private slowTimeout: ReturnType<typeof setTimeout> | undefined;

    constructor(private readonly host: ReactiveControllerHost & HandinOptions) {
        host.addController(this);
    }

    /** Where the hand-in is: IDLE when none is in progress. */
    get progress(): HandinProgress {
        return this._progress;
    }

    /**
     * The submission being judged with its latest pending status, from when the server accepts it until judging ends;
     * null when idle. `judge` resolves to the judged submission.
     */
    get submission(): SubmissionStatus | null {
        return this._submission;
    }

    /** Whether a hand-in is in progress. */
    get busy(): boolean {
        return inProgress(this._progress);
    }

    /**
     * Posts `code`. Once the server accepts it, the submission is queued and `judge` is expected to follow.
     * Throws when a hand-in is already in progress.
     */
    async handIn(code: string): Promise<HandedIn> {
        if (this.busy) throw new Error("A hand-in is already in progress.");
        if (new Blob([code]).size > MAX_CODE_BYTES) {
            return { ok: false, message: I18n.t("js.submission-too-long") };
        }
        this.start();
        let response: Response | undefined;
        let responseText: string | undefined;
        let data: { id: number; number: number | null; series_id: number | null };
        try {
            response = await this.post(code);
            responseText = await response.text();
            data = JSON.parse(responseText);
        } catch (error) {
            let message: string;
            if (error instanceof ResponseError) {
                message = await getErrorMessage(error.response);
            } else if (error instanceof NetworkError) {
                message = I18n.t("js.submission-network");
            } else {
                message = I18n.t("js.submission-failed");
                Sentry.withScope((scope) => {
                    scope.setExtras({
                        exerciseId: this.host.exerciseId,
                        courseId: this.host.courseId,
                        seriesId: this.host.seriesId,
                        programmingLanguage: this.host.programmingLanguage,
                        responseStatus: response?.status,
                        contentLengthHeader: response?.headers?.get("content-length") ?? "no response/content-length",
                        responseLength: responseText?.length ?? "no response text",
                        responseText: String(responseText?.substring(0, 1000)),
                    });
                    Sentry.captureException(error);
                });
            }
            this.stop();
            return { ok: false, message };
        }
        const submission: SubmissionStatus = {
            id: data.id,
            number: data.number,
            status: "queued",
            summary: null,
            accepted: false,
            created_at: new Date().toISOString(),
        };
        this.setSubmission(submission);
        this.setProgress({ phase: "queued" });
        return { ok: true, submission, seriesId: data.series_id };
    }

    /**
     * Polls the status of `submission`, which `handIn` accepted, until it is judged. Fails when polling gives up or a
     * status request fails with a client error.
     */
    async judge(submission: SubmissionStatus): Promise<Judged> {
        try {
            return await this.poll(submission.id);
        } finally {
            this.stop();
        }
    }

    /** Clears the slow timer when the card leaves the page. Polling, if any, runs to its end: giving up bounds it. */
    hostDisconnected(): void {
        clearTimeout(this.slowTimeout);
    }

    private async poll(id: number): Promise<Judged> {
        let waited = 0;
        let backoff = 0;
        let failingFor = 0;
        for (;;) {
            const delay = backoff || pollDelay(waited);
            await new Promise((resolve) => setTimeout(resolve, delay));
            waited += delay;
            try {
                const response = await fetch(`/submissions/${id}/status.json`);
                const data: SubmissionStatus = await response.json();
                backoff = 0;
                failingFor = 0;
                if (!isPending(data.status)) return { ok: true, submission: data };
                this.setSubmission(data);
                this.setProgress({ phase: data.status });
            } catch (error) {
                failingFor += delay;
                backoff = Math.min(delay * 2, MAX_BACKOFF_MS);
                const clientError = error instanceof ResponseError && error.response.status < 500;
                if (clientError || failingFor >= GIVE_UP_AFTER_MS) {
                    const message =
                        error instanceof NetworkError
                            ? I18n.t("js.submission-network")
                            : I18n.t("js.submission-refresh-failed");
                    return { ok: false, message };
                }
            }
        }
    }

    private post(code: string): Promise<Response> {
        return fetch("/submissions.json", {
            method: "POST",
            body: JSON.stringify({
                submission: {
                    code: code,
                    exercise_id: this.host.exerciseId,
                    course_id: this.host.courseId,
                    series_id: this.host.seriesId,
                },
                natural_language: this.host.naturalLanguage,
            }),
            headers: {
                "Content-type": "application/json",
            },
        });
    }

    private setProgress(change: Partial<HandinProgress>): void {
        const progress = { ...this._progress, ...change };
        if (progress.phase === this._progress.phase && progress.slow === this._progress.slow) return;
        this._progress = progress;
        this.host.requestUpdate();
    }

    private setSubmission(submission: SubmissionStatus | null): void {
        if (submission?.id === this._submission?.id && submission?.status === this._submission?.status) return;
        this._submission = submission;
        this.host.requestUpdate();
    }

    private start(): void {
        this.setProgress({ phase: "submitting", slow: false });
        this.slowTimeout = setTimeout(() => {
            this.setProgress({ slow: true });
        }, SLOW_THRESHOLD_MS);
    }

    private stop(): void {
        clearTimeout(this.slowTimeout);
        this.setProgress(IDLE);
        this.setSubmission(null);
    }
}
